import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const [state, setState] = useState(result === "success" ? "checking" : "cancelled");

  useEffect(() => {
    const sid = params.get("session_id");
    if (result !== "success" || !sid) return;
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); qc.invalidateQueries(); return; }
        if (["expired", "failed"].includes(data.status)) return setState("failed");
      } catch { /* keep polling */ }
      if (!stop && ++tries < 15) setTimeout(poll, 2000); else if (!stop) setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, [result, params, api, qc]);

  const view = {
    checking: [Loader2, "text-indigo-400 animate-spin", t("pay_checking")],
    paid: [CheckCircle2, "text-emerald-400", t("pay_success")],
    pending: [Loader2, "text-amber-400", t("pay_pending")],
    failed: [XCircle, "text-rose-400", t("pay_failed")],
    cancelled: [XCircle, "text-slate-400", t("pay_cancelled")],
  }[state];
  const [Icon, cls, msg] = view;
  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="text-center max-w-md fade-up" data-testid={`payment-${state}`}>
        <Icon className={`h-14 w-14 mx-auto ${cls}`} />
        <p className="font-display text-3xl font-black mt-6">{msg}</p>
        <Link to="/dashboard/billing" className="inline-block mt-8 rounded-full bg-indigo-600 px-6 py-3 font-semibold hover:bg-indigo-500 transition-colors" data-testid="payment-back-btn">{t("back_billing")}</Link>
      </div>
    </div>
  );
}
